/**
 * SnapshotCodeViewer — read-only code viewer with syntax highlighting & folding
 *
 * Uses a lightweight CodeMirror setup (read-only, no extensions) for rendering
 * Markdown content with proper highlighting, folding, and line numbers.
 */

import { useEffect, useRef } from "react";
import { EditorView, lineNumbers } from "@codemirror/view";
import { EditorState } from "@codemirror/state";
import { markdown } from "@codemirror/lang-markdown";
import { foldGutter, syntaxHighlighting, defaultHighlightStyle } from "@codemirror/language";

interface Props {
    content: string;
}

export function SnapshotCodeViewer({ content }: Props) {
    const containerRef = useRef<HTMLDivElement>(null);
    const viewRef = useRef<EditorView | null>(null);

    useEffect(() => {
        if (!containerRef.current) return;

        // Clean up existing view
        if (viewRef.current) {
            viewRef.current.destroy();
        }

        // Create read-only editor state with minimal extensions
        const state = EditorState.create({
            doc: content,
            extensions: [
                markdown(),
                foldGutter(),
                syntaxHighlighting(defaultHighlightStyle),
                EditorView.editable.of(false), // Read-only
                lineNumbers(),
            ],
        });

        // Create read-only editor view
        const view = new EditorView({
            state,
            parent: containerRef.current,
        });

        viewRef.current = view;

        return () => {
            view.destroy();
            viewRef.current = null;
        };
    }, [content]);

    return <div ref={containerRef} className="h-full w-full overflow-hidden" />;
}
